import type { IPoint, ISize } from '@/interfaces/geometry';
import { EPosition } from 'common/enum/misc.enum';
import { clampNumber } from 'common/utils/math.utils';

/** Keep a popover's pointer aligned with its anchor after viewport clamping. */
export const getPopoverArrowStyle = (
  anchor: Element | null,
  content: Element | null,
  arrowSize: number,
): Record<string, string> => {
  if (!anchor || !content) return {};
  const anchorRect = anchor.getBoundingClientRect();
  const contentRect = content.getBoundingClientRect();
  const anchorCenterX = anchorRect.left + anchorRect.width / 2 - contentRect.left;
  const left = clampNumber(anchorCenterX - arrowSize / 2, 0, Math.max(contentRect.width - arrowSize, 0));
  return { left: `${Math.round(left)}px` };
};

/**
 * Calculate the position of an element relative to another element
 */
export const calculateElementPosition = (params: {
  anchorElement: Element | null;
  element: Element | null;
  offsetX?: number;
  offsetY?: number;
  fullWidth?: boolean;
  position: EPosition;
}): Record<string, string> => {
  const style: Record<string, string> = {};
  const anchorRect = params.anchorElement?.getBoundingClientRect();
  const elementRect = params.element?.getBoundingClientRect();
  if (!params.element || !elementRect) return style;

  const offsetX = params.offsetX ?? 0;
  const offsetY = params.offsetY ?? 0;

  // Center on viewport regardless of anchor
  if (params.position === EPosition.CENTER) {
    const viewportTop = (window.scrollY || window.pageYOffset || 0) + (window.innerHeight - elementRect.height) / 2;
    const viewportLeft = (window.scrollX || window.pageXOffset || 0) + (window.innerWidth - elementRect.width) / 2;
    style.top = `${viewportTop + offsetY}px`;
    style.left = `${viewportLeft + offsetX}px`;
    return style;
  }

  if (!anchorRect) return style;

  switch (params.position) {
    case EPosition.BOTTOM_LEFT:
      style.top = `${anchorRect.top + anchorRect.height + offsetY}px`;
      style.left = `${anchorRect.right - elementRect.width + offsetX}px`;
      break;
    case EPosition.BOTTOM_RIGHT:
      style.top = `${anchorRect.top + anchorRect.height + offsetY}px`;
      style.left = `${anchorRect.left + offsetX}px`;
      break;
    case EPosition.BOTTOM_CENTER:
      style.top = `${anchorRect.top + anchorRect.height + offsetY}px`;
      style.left = `${anchorRect.left + (anchorRect.width - elementRect.width) / 2 + offsetX}px`;
      break;
    case EPosition.TOP_CENTER:
      style.top = `${anchorRect.top - elementRect.height - offsetY}px`;
      style.left = `${anchorRect.left + (anchorRect.width - elementRect.width) / 2 + offsetX}px`;
      break;
    case EPosition.TOP_LEFT:
      style.top = `${anchorRect.top - elementRect.height - offsetY}px`;
      style.left = `${anchorRect.right - elementRect.width + offsetX}px`;
      break;
    case EPosition.TOP_RIGHT:
      style.top = `${anchorRect.top - elementRect.height - offsetY}px`;
      style.left = `${anchorRect.left + offsetX}px`;
      break;
    case EPosition.LEFT:
      style.top = `${anchorRect.top + (anchorRect.height - elementRect.height) / 2 + offsetY}px`;
      style.left = `${anchorRect.left - elementRect.width - offsetX}px`;
      break;
    case EPosition.RIGHT:
      style.top = `${anchorRect.top + (anchorRect.height - elementRect.height) / 2 + offsetY}px`;
      style.left = `${anchorRect.right + offsetX}px`;
      break;
  }

  if (params.fullWidth) {
    style.left = `${anchorRect.left}px`;
    style.width = `${anchorRect.width}px`;
  }

  return style;
};

export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export const isFullyVisibleX = (container: HTMLElement, item: HTMLElement): boolean => {
  const c = container.getBoundingClientRect();
  const r = item.getBoundingClientRect();
  return r.left >= c.left && r.right <= c.right;
};

/** Keeps teleported overlays inside the topmost dialog that contains their trigger or rendered panel. */
export const resolveDialogTeleportTarget = (
  anchor?: HTMLElement | null,
  container?: HTMLElement | null,
): HTMLElement | true =>
  anchor?.closest<HTMLElement>('dialog') ??
  container?.querySelector<HTMLElement>('dialog') ??
  container?.closest<HTMLElement>('dialog') ??
  true;

export const openUrlInNewTab = (url?: string | null): void => {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

/** Adds a window listener and returns its removal, for listeners that only live as long as a gesture. */
export const listenOnWindow = <K extends keyof WindowEventMap>(
  type: K,
  handler: (event: WindowEventMap[K]) => void,
  options?: AddEventListenerOptions,
): (() => void) => {
  window.addEventListener(type, handler, options);
  return () => window.removeEventListener(type, handler, options);
};

export const preventDefault = (event: Event): void => event.preventDefault();

/** Layout size, which ignores transforms: an element that is lifted or still animating measures as it rests. */
export const getElementSize = (element: HTMLElement): ISize => ({
  width: element.offsetWidth,
  height: element.offsetHeight,
});

export const getPointerPoint = (event: Pick<PointerEvent, 'clientX' | 'clientY'>): IPoint => ({
  x: event.clientX,
  y: event.clientY,
});
